/**
 * D-033 - which resources a still references, and the still with them swapped
 * for frozen copies.
 *
 * Pure, and shared on purpose: the SDK reads the list at capture to know what
 * to freeze, the shell rewrites the tree before rebuilding it, and a unit test
 * reads both - so "what counts as a resource" is one function, not two that
 * drift. The store itself is `server/resources.ts`.
 *
 * The tree is rrweb-snapshot's serialized document. rrweb has already made
 * every URL absolute at capture (`absoluteToDoc`, and `absolutifyURLs` inside
 * the CSS it inlines), so a URL here is compared as a string.
 *
 * What is a resource: what the rebuilt still would fetch from the reviewed
 * application when it is shown - images, posters, icons, a stylesheet that
 * could not be inlined, and every `url()` in CSS (fonts, backgrounds). Never a
 * script: the still runs none (F-009), so a script is not part of the picture.
 */

export interface StillNode {
  type?: number;
  tagName?: string;
  attributes?: Record<string, unknown>;
  textContent?: string;
  isStyle?: boolean;
  childNodes?: StillNode[];
}

const ELEMENT = 2;
const TEXT = 3;
const CSS_URL = /url\(\s*(['"]?)([^'")]*?)\1\s*\)/g;
const isFetchable = (u: string) => /^https?:\/\//i.test(u);

/** The attributes of one element that name a resource, by what kind of value they hold. */
function slotsOf(tag: string, attributes: Record<string, unknown>): { url: string[]; srcset: string[]; css: string[] } {
  const url: string[] = [];
  if (tag !== "script" && tag !== "iframe") {
    if (typeof attributes.src === "string") url.push("src");
    if (typeof attributes.poster === "string") url.push("poster");
  }
  if (tag === "link" && typeof attributes.href === "string") {
    const rel = String(attributes.rel ?? "").toLowerCase();
    // A stylesheet rrweb inlined is in `_cssText`; its href is not fetched again.
    if (/\bicon\b/.test(rel) || (/\bstylesheet\b/.test(rel) && typeof attributes._cssText !== "string")) url.push("href");
  }
  if (tag === "image") {
    if (typeof attributes.href === "string") url.push("href");
    if (typeof attributes["xlink:href"] === "string") url.push("xlink:href");
  }
  const srcset = typeof attributes.srcset === "string" ? ["srcset"] : [];
  const css = ["style", "_cssText"].filter((k) => typeof attributes[k] === "string");
  return { url, srcset, css };
}

const srcsetUrls = (value: string): string[] =>
  value
    .split(",")
    .map((part) => part.trim().split(/\s+/)[0] ?? "")
    .filter(Boolean);

const cssUrls = (text: string): string[] => Array.from(text.matchAll(CSS_URL), (m) => m[2].trim());

function walk(node: StillNode | undefined, visit: (node: StillNode) => void): void {
  if (!node || typeof node !== "object") return;
  visit(node);
  for (const child of node.childNodes ?? []) walk(child, visit);
}

/** Every http(s) URL a still would fetch when shown, each once, in the order met. */
export function resourceUrls(root: unknown): string[] {
  const found = new Set<string>();
  const add = (u: string) => {
    if (isFetchable(u)) found.add(u);
  };
  walk(root as StillNode, (node) => {
    if (node.type === ELEMENT && node.attributes) {
      const tag = String(node.tagName ?? "").toLowerCase();
      const slots = slotsOf(tag, node.attributes);
      for (const k of slots.url) add(String(node.attributes[k]));
      for (const k of slots.srcset) srcsetUrls(String(node.attributes[k])).forEach(add);
      for (const k of slots.css) cssUrls(String(node.attributes[k])).forEach(add);
    } else if (node.type === TEXT && node.isStyle && typeof node.textContent === "string") {
      cssUrls(node.textContent).forEach(add);
    }
  });
  return [...found];
}

/**
 * A copy of the still with every URL in `map` replaced by `to(hash)`. URLs the
 * map does not name stay as they are - the second class, fetched from where
 * they were. The input is not touched: the caller may hold it for a redraw.
 */
export function withResources(root: unknown, map: Record<string, string>, to: (hash: string) => string): unknown {
  if (!root || typeof root !== "object" || Object.keys(map).length === 0) return root;
  const swap = (u: string) => (Object.prototype.hasOwnProperty.call(map, u) ? to(map[u]) : u);
  const swapCss = (text: string) =>
    text.replace(CSS_URL, (whole, quote: string, u: string) => {
      const next = swap(u.trim());
      return next === u.trim() ? whole : `url(${quote}${next}${quote})`;
    });
  const copy = structuredClone(root) as StillNode;
  walk(copy, (node) => {
    if (node.type === ELEMENT && node.attributes) {
      const a = node.attributes;
      const slots = slotsOf(String(node.tagName ?? "").toLowerCase(), a);
      for (const k of slots.url) a[k] = swap(String(a[k]));
      for (const k of slots.srcset)
        a[k] = String(a[k])
          .split(",")
          .map((part) => {
            const [u, ...rest] = part.trim().split(/\s+/);
            return [swap(u ?? ""), ...rest].join(" ");
          })
          .join(", ");
      for (const k of slots.css) a[k] = swapCss(String(a[k]));
    } else if (node.type === TEXT && node.isStyle && typeof node.textContent === "string") {
      node.textContent = swapCss(node.textContent);
    }
  });
  return copy;
}

const HASH = /^[0-9a-f]{64}$/;

/** The map a stored still carries on its root (`loupe_resources`, URL to sha256), held to that shape. */
export function resourceMap(root: unknown): Record<string, string> {
  const map = (root as { loupe_resources?: unknown } | null)?.loupe_resources;
  if (!map || typeof map !== "object" || Array.isArray(map)) return {};
  return Object.fromEntries(
    Object.entries(map as Record<string, unknown>).filter((e): e is [string, string] => typeof e[1] === "string" && HASH.test(e[1])),
  );
}

/** One resource as the capture froze it: its bytes as base64, named by their sha256. */
export interface FrozenResource {
  url: string;
  hash: string;
  type: string;
  data: string;
}

/** What may be frozen - the server holds the same list (`storableType` in server/resources.ts). */
export function freezableType(type: string): string | null {
  const t = type.split(";")[0].trim().toLowerCase();
  if (/^(image|font)\/[a-z0-9.+-]+$/.test(t) || t === "text/css") return t;
  if (/^application\/(font-woff2?|x-font-[a-z0-9]+|vnd\.ms-fontobject|font-sfnt|octet-stream)$/.test(t)) return t;
  return null;
}

const toHex = (buffer: ArrayBuffer) => Array.from(new Uint8Array(buffer), (b) => b.toString(16).padStart(2, "0")).join("");

function toBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let text = "";
  for (let i = 0; i < bytes.length; i += 0x8000) text += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(text);
}

/**
 * Fetches what a still references, at capture, from where the page fetched it
 * - the browser's cache usually answers - and hashes it. Never throws and
 * never waits past its budget: a resource that does not arrive in time, is
 * too large, or is not an image, a font or a stylesheet stays a URL in the
 * still. An HTML answer is refused on purpose: a single-page application
 * answers a removed asset with 200 and its index page (measured 2026-10-21).
 */
export async function freezeResources(
  urls: string[],
  { fetchImpl = fetch, budgetMs = 4000, maxBytes = 8 * 1024 * 1024, maxTotal = 24 * 1024 * 1024 } = {},
): Promise<FrozenResource[]> {
  const until = Date.now() + budgetMs;
  const timer = new AbortController();
  const stop = setTimeout(() => timer.abort(), budgetMs);
  let total = 0;
  const one = async (url: string): Promise<FrozenResource | null> => {
    try {
      // Cookies on the application's own origin; a font host that allows any origin (`*`) answers only without them.
      const response = await fetchImpl(url, { credentials: "same-origin", signal: timer.signal });
      const type = freezableType(response.headers.get("content-type") ?? "");
      if (!response.ok || !type) return null;
      const buffer = await response.arrayBuffer();
      if (buffer.byteLength === 0 || buffer.byteLength > maxBytes || total + buffer.byteLength > maxTotal || Date.now() > until) return null;
      total += buffer.byteLength;
      return { url, hash: toHex(await crypto.subtle.digest("SHA-256", buffer)), type, data: toBase64(buffer) };
    } catch {
      return null;
    }
  };
  try {
    return (await Promise.all(urls.map(one))).filter((r): r is FrozenResource => r !== null);
  } finally {
    clearTimeout(stop);
  }
}
